import { useEffect, useState } from "react";
import { CurrencyDollarSimple, Warning, FloppyDisk, ClockCounterClockwise, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { invalidateFuelCalculatorSettings } from "@/lib/fuelCalculatorSettings";

/**
 * TaxPricingSettingsPanel — V1.1.1 · OPS-002.
 *
 * Single source of truth for every fuel pricing calculation on the
 * platform. Backed by `db.settings._id="tax_pricing"` with an
 * effective-date history table so future retroactive re-pricing can
 * look up the rate that was in effect at any past moment.
 *
 * Editable by Administrator / Owner only. The Fuel Pricing Calculator
 * (Quick Calculator → Fuel Tools tab) reads these values on every
 * popover open.
 */

const ROUNDING_RULES = [
  { value: "none",         label: "None — display raw" },
  { value: "ends_in_nine", label: "Ends in 9 (industry convention)" },
  { value: "nearest_cent", label: "Nearest cent (2 decimals)" },
];

export default function TaxPricingSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [current, hist] = await Promise.all([
        api.get("/settings/tax-pricing"),
        api.get("/admin/settings/tax-pricing/history").catch(() => ({ data: [] })),
      ]);
      setSettings(current.data);
      setHistory(hist.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tax & pricing settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const change = (patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/tax-pricing", settings);
      setSettings(r.data);
      setDirty(false);
      invalidateFuelCalculatorSettings();
      toast.success("Tax & pricing settings saved");
      // Reload history so the new snapshot appears.
      const hist = await api.get("/admin/settings/tax-pricing/history");
      setHistory(hist.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
    );
  }

  return (
    <div data-testid="admin-panel-tax-pricing">
      <div className="mb-4 border-b border-slate-200 pb-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
          <CurrencyDollarSimple size={18} weight="bold" /> Tax &amp; Pricing Settings
        </h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Single source of truth for every fuel pricing calculation. The Fuel
          Pricing Calculator reads these values automatically — never
          hard-coded. Every change is snapshotted into an effective-date
          history for future retroactive re-pricing.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <NumberField
          testid="tax-pricing-ontario-road-tax"
          label="Ontario Road Tax"
          suffix="$ / L"
          value={settings.ontario_road_tax}
          onChange={(v) => change({ ontario_road_tax: v })}
          step="0.001"
        />
        <NumberField
          testid="tax-pricing-federal-carbon-tax"
          label="Federal Carbon Tax"
          suffix="$ / L"
          value={settings.federal_carbon_tax}
          onChange={(v) => change({ federal_carbon_tax: v })}
          step="0.001"
        />
        <NumberField
          testid="tax-pricing-hst-rate"
          label="HST Rate"
          suffix="decimal (e.g. 0.13 for 13%)"
          value={settings.hst_rate}
          onChange={(v) => change({ hst_rate: v })}
          step="0.001"
          max="0.999"
        />
        <NumberField
          testid="tax-pricing-decimal-precision"
          label="Decimal Precision"
          suffix="display digits (0–8)"
          value={settings.decimal_precision}
          onChange={(v) => change({ decimal_precision: parseInt(v, 10) || 0 })}
          step="1"
          min="0"
          max="8"
          integer
        />

        <div className="rounded-md border border-slate-200 bg-white p-3">
          <label className="flex items-start gap-2">
            <input
              data-testid="tax-pricing-carbon-tax-enabled"
              type="checkbox"
              checked={settings.carbon_tax_enabled}
              onChange={(e) => change({ carbon_tax_enabled: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-slate-400"
            />
            <span>
              <span className="block text-sm font-medium text-slate-900">
                Carbon Tax Enabled
              </span>
              <span className="block text-[11px] text-slate-500">
                Uncheck to temporarily exclude the federal carbon tax from
                calculations without losing the configured amount.
              </span>
            </span>
          </label>
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Selling Price Rounding Rule
          </label>
          <select
            data-testid="tax-pricing-rounding-rule"
            value={settings.selling_price_rounding_rule}
            onChange={(e) => change({ selling_price_rounding_rule: e.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-brand focus:outline-none"
          >
            {ROUNDING_RULES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
        <div className="text-[11px] text-slate-500">
          Effective from{" "}
          <span className="font-mono text-slate-700">
            {settings.effective_from ? formatIso(settings.effective_from) : "—"}
          </span>
          {settings.updated_by && (
            <> · updated by <span className="font-mono">{settings.updated_by}</span></>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="tax-pricing-history-toggle"
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <ClockCounterClockwise size={13} weight="bold" />
            {showHistory ? "Hide History" : `History (${history.length})`}
          </button>
          <button
            data-testid="tax-pricing-reload"
            onClick={load}
            disabled={loading}
            className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            aria-label="Reload"
          >
            <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : undefined} />
          </button>
          <button
            data-testid="tax-pricing-save"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
          >
            <FloppyDisk size={13} weight="bold" />
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {dirty && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
          <Warning size={12} weight="fill" />
          Unsaved changes. Saving snapshots these values into the effective-date
          history — the current Fuel Pricing Calculator will read the new rates
          the next time it opens.
        </div>
      )}

      {showHistory && (
        <div
          data-testid="tax-pricing-history-panel"
          className="mt-3 overflow-hidden rounded-md border border-slate-200"
        >
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Effective-date history
          </div>
          {history.length === 0 ? (
            <div className="p-3 text-center text-xs text-slate-500">
              No historical snapshots yet — save the current settings to create
              the first entry.
            </div>
          ) : (
            <ol className="divide-y divide-slate-100 text-xs">
              {history.map((h, idx) => (
                <li
                  key={`${h.effective_from}-${idx}`}
                  data-testid={`tax-pricing-history-row-${idx}`}
                  className="grid grid-cols-1 gap-1 px-3 py-2 md:grid-cols-[180px_1fr_auto]"
                >
                  <div className="font-mono text-slate-700">
                    {formatIso(h.effective_from)}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                    <Chip>Road ${h.ontario_road_tax}</Chip>
                    <Chip>Carbon {h.carbon_tax_enabled ? `$${h.federal_carbon_tax}` : "off"}</Chip>
                    <Chip>HST {(h.hst_rate * 100).toFixed(2)}%</Chip>
                    <Chip>Round {h.selling_price_rounding_rule}</Chip>
                  </div>
                  <div className="text-right text-[10px] text-slate-500">
                    {h.updated_by || "—"}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function NumberField({ testid, label, suffix, value, onChange, integer, ...rest }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          data-testid={testid}
          type="number"
          inputMode={integer ? "numeric" : "decimal"}
          value={value ?? ""}
          onChange={(e) => onChange(integer ? e.target.value : parseFloat(e.target.value))}
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-right font-mono text-sm text-slate-900 focus:border-brand focus:outline-none"
          {...rest}
        />
      </div>
      {suffix && (
        <div className="mt-0.5 text-[10px] text-slate-500">{suffix}</div>
      )}
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
      {children}
    </span>
  );
}

function formatIso(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-CA", { hour12: false });
}
